// Deferred by design: index.js imports this module, so these are only ever
// dereferenced inside settings(), which runs long after both files are evaluated.
import { getBillingTabs, getGeneralTabs, getLogTabs, getVisibleSettingsSections } from './index';

export default {
    routes: [
        {
            path: 'settings',
            name: 'settings',
            component: () => import('@/pages/organization/settings/Settings.vue'),
            redirect: { name: 'settings-organization' },
            children: [] // Populated dynamically via getSettingsRoutes() in index.js
        },
        // Custom Field Builder — redirects to Object Manager
        {
            path: 'settings/custom-fields/:entityType',
            name: 'settings-custom-field-builder',
            component: () => import('@/pages/organization/settings/CustomFieldBuilder.vue'),
            props: true
        },
        // Object Manager (full page, like Custom Field Builder)
        {
            path: 'settings/objects/:objectType',
            name: 'settings-object-editor',
            component: () => import('@/pages/organization/settings/objects/Editor.vue'),
            props: true
        },
        // List column editor (full page, like Object Manager)
        {
            path: 'settings/lists/:objectType',
            name: 'settings-list-editor',
            component: () => import('@/pages/organization/settings/lists/Editor.vue'),
            props: true
        }
    ],
    settingsRoutes: [
        {
            path: 'organization',
            name: 'settings-organization',
            component: () => import('@/pages/organization/settings/Organization.vue'),
            props: true
        },
        {
            path: 'branding',
            name: 'settings-branding',
            component: () => import('@/pages/organization/settings/Branding.vue'),
            props: true
        },
        {
            path: 'users',
            name: 'settings-user-list',
            component: () => import('@/pages/organization/settings/users/List.vue'),
            children: [
                {
                    path: ':id([ui]\\d+)',
                    name: 'settings-user-preview',
                    component: () => import('@/pages/organization/settings/users/Preview.vue'),
                    props: true
                }
            ]
        },
        {
            path: 'modules',
            name: 'settings-modules',
            component: () => import('@/pages/organization/settings/Modules.vue'),
            props: true
        },
        {
            path: 'roles',
            name: 'settings-roles',
            component: () => import('@/pages/organization/settings/roles/List.vue'),
            props: true
        },
        {
            // Shell for every read-only trail: its tabs come from logTabs(), so a module
            // adds its own log screen without this file knowing about it.
            path: 'logs',
            name: 'settings-logs',
            component: () => import('@/pages/organization/settings/Logs.vue'),
            children: [] // populated by getOrgRoutes() via logRoutes injection
        },
        {
            path: 'ai-connections',
            name: 'settings-ai-connections',
            component: () => import('@/pages/organization/settings/AiConnections.vue'),
            props: true
        },
        {
            // A shell with its own tab rail: what every organization configures
            // whatever it bought — the application's behaviour, the calendar,
            // the tasks and the document storage. Its children come partly from
            // here and partly from the modules, via `generalRoutes`.
            path: 'general',
            name: 'settings-general',
            component: () => import('@/pages/organization/settings/General.vue'),
            redirect: { name: 'settings-general-main' },
            children: [
                {
                    // Not the empty path: an empty child's address is a prefix
                    // of its siblings', and the rail lit this tab alongside
                    // whichever one was really open. The parent redirects here
                    // by name, so the bookmarked `/settings/general` still
                    // lands in the right place.
                    path: 'preferences',
                    name: 'settings-general-main',
                    component: () => import('@/pages/organization/settings/general/GeneralSettings.vue'),
                    props: true
                },
                {
                    path: 'drive',
                    name: 'settings-general-drive',
                    component: () => import('@/pages/organization/settings/Drive.vue')
                }
            ]
        },
        {
            path: 'operations',
            name: 'settings-operations',
            component: () => import('@/pages/organization/settings/Operations.vue'),
            redirect: { name: 'settings-operations-work' },
            children: [] // populated by getOrgRoutes() via operationsRoutes injection
        },
        {
            path: 'integrations',
            name: 'settings-integrations',
            component: () => import('@/pages/organization/settings/Integrations.vue'),
            children: [] // populated by getOrgRoutes() via integrationRoutes injection
        },
        {
            // What the organization consumes and what it owes for it. Both used
            // to sit in the main navigation as if they were applications; their
            // abilities have said `settings.usage` and `settings.billing` from
            // the start. One entry with two tabs rather than two entries, so the
            // settings list does not grow by two lines for one subject.
            path: 'billing',
            name: 'settings-billing',
            component: () => import('@/pages/organization/settings/Billing.vue'),
            redirect: { name: 'settings-billing-usage' },
            children: [
                {
                    // A path of its own, not the empty one: an empty child's
                    // address is a *prefix* of its siblings', so the rail's
                    // `isActive` lit both tabs at once. Siblings with real
                    // segments cannot be prefixes of each other.
                    path: 'usage',
                    name: 'settings-billing-usage',
                    component: () => import('@/pages/organization/settings/billing/Usage.vue')
                },
                {
                    path: 'subscription',
                    name: 'settings-billing-subscription',
                    component: () => import('@/pages/organization/settings/billing/Subscription.vue')
                },
                {
                    path: 'invoices',
                    name: 'settings-billing-invoices',
                    component: () => import('@/pages/organization/settings/billing/Invoices.vue')
                }
                // more populated by getOrgRoutes() via billingRoutes injection
            ]
        },
        {
            path: 'objects',
            name: 'settings-objects',
            component: () => import('@/pages/organization/settings/objects/List.vue')
        },
        {
            path: 'lists',
            name: 'settings-lists',
            component: () => import('@/pages/organization/settings/lists/List.vue')
        }
    ],
    logRoutes: [
        {
            path: 'history',
            name: 'settings-logs-history',
            component: () => import('@/pages/organization/settings/logs/History.vue'),
            props: true
        },
        {
            path: 'mcp-audit',
            name: 'settings-mcp-audit',
            component: () => import('@/pages/organization/settings/logs/McpAuditLogs.vue'),
            props: true
        },
        {
            path: 'imports',
            name: 'settings-imports',
            component: () => import('@/pages/organization/settings/imports/List.vue')
        },
        {
            path: 'imports/:id(\\d+)',
            name: 'settings-import-detail',
            component: () => import('@/pages/organization/settings/imports/Detail.vue'),
            props: true
        },
        {
            path: 'sync-review',
            name: 'settings-sync-review',
            component: () => import('@/pages/organization/settings/logs/SyncDedupReview.vue'),
            props: true
        }
    ],
    billingTabs: () => [
        {
            position: 10,
            title: i18n.t('Usage'),
            icon: 'chart-timeline-variant',
            route: 'settings-billing-usage',
            ability: 'settings.usage'
        },
        {
            position: 15,
            title: i18n.t('Subscription'),
            icon: 'file-sign',
            route: 'settings-billing-subscription',
            ability: 'settings.billing'
        },
        {
            position: 20,
            title: i18n.t('Billing'),
            icon: 'receipt-text-outline',
            route: 'settings-billing-invoices',
            ability: 'settings.billing'
        }
    ],
    generalTabs: () => [
        {
            position: 1,
            title: i18n.t('General'),
            icon: 'tune',
            route: 'settings-general-main',
            ability: 'settings.preferences'
        },
        {
            position: 10,
            title: i18n.t('Files'),
            icon: 'folder-cog-outline',
            route: 'settings-general-drive',
            ability: 'settings.documents'
        }
    ],
    logTabs: () => [
        { title: i18n.t('History'), icon: 'format-list-bulleted', route: 'settings-logs-history', admin: true },
        {
            title: i18n.t('MCP Activity Log'),
            icon: 'robot-confused-outline',
            route: 'settings-mcp-audit',
            admin: true
        },
        { title: i18n.t('Imports'), icon: 'file-import', route: 'settings-imports', admin: true },
        {
            title: i18n.t('Duplicate review'),
            icon: 'account-multiple-check-outline',
            route: 'settings-sync-review',
            admin: true
        }
    ],
    settings: () => [
        {
            title: i18n.t('Settings'),
            order: 10,
            items: [
                {
                    title: i18n.t('Organization'),
                    icon: 'sitemap',
                    route: 'settings-organization',
                    permission: 'organization'
                },
                {
                    // Right under Organization: the two entries answer the same
                    // question — how this organization behaves — and reading one
                    // without seeing the other is what made Files feel lost.
                    // Shown as soon as one of its tabs is readable, the way Logs is.
                    title: i18n.t('General'),
                    icon: 'tune',
                    route: 'settings-general',
                    visible: () => getGeneralTabs().length > 0
                },
                {
                    // Shown as soon as one of its tabs is readable, the way
                    // General is: someone who may read the consumption without
                    // the invoices still has a reason to open it.
                    title: i18n.t('Usage and billing'),
                    icon: 'credit-card-outline',
                    route: 'settings-billing',
                    visible: () => getBillingTabs().length > 0
                },
                { title: i18n.t('Branding'), icon: 'palette', route: 'settings-branding', admin: true },
                {
                    // The MCP endpoint is what the AI Assistant module sells; an
                    // organization that did not buy it has nothing to connect to.
                    title: i18n.t('AI Connections'),
                    icon: 'robot-outline',
                    route: 'settings-ai-connections',
                    module: 'ai_assistant'
                }
            ]
        },
        {
            title: i18n.t('Applications'),
            order: 10,
            items: [
                // Files is a tab of General now, and Operations went back to
                // being declared by Work Orders — it holds nothing else.
            ]
        },
        {
            title: i18n.t('Administration'),
            order: 30,
            items: [
                { title: i18n.t('Users'), icon: 'account-group', route: 'settings-user-list', permission: 'users' },
                { title: i18n.t('Roles'), icon: 'shield-account', route: 'settings-roles', permission: 'roles' },
                { title: i18n.t('Modules'), icon: 'puzzle', route: 'settings-modules', admin: true },
                { title: i18n.t('Objects Manager'), icon: 'shape-outline', route: 'settings-objects', admin: true },
                { title: i18n.t('Lists Manager'), icon: 'table-column', route: 'settings-lists', admin: true },
                {
                    title: i18n.t('Integrations'),
                    icon: 'transit-connection-variant',
                    route: 'settings-integrations',
                    admin: true
                },
                {
                    // One entry for every read-only trail; the screens themselves are the
                    // vertical tabs of settings-logs, declared in logTabs(). Gated on the
                    // tabs rather than on `admin`, so a role holding only the ledger trail
                    // still reaches it.
                    title: i18n.t('Logs'),
                    icon: 'text-box-search-outline',
                    route: 'settings-logs',
                    visible: () => getLogTabs().length > 0
                }
            ]
        }
    ],
    nav: () => ({
        section: 'bottom',
        items: [
            {
                position: 10,
                title: i18n.t('Settings'),
                icon: 'cog',
                to: { name: 'settings' },
                // A door onto an empty room is worse than no door: the person
                // clicks, lands on a screen with nothing in it, and reads the
                // application as broken rather than as not theirs.
                visible: () => getVisibleSettingsSections().length > 0
            }
        ]
    })
};
